import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/store";
import { Plus, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { iconFor, tintFor } from "@/lib/categoryIcons";

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const add = useCart((s) => s.add);

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => setData(r.data));
  }, [productId]);

  if (!data) return <div className="text-[var(--text-2)]">Loading…</div>;
  const { product, reviews } = data;
  const { icon: Icon, colour } = iconFor(product.category);

  const onAdd = () => {
    add(product, product.store_id);
    toast.success(`${product.name} added to basket`);
  };

  return (
    <div className="space-y-10">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm font-bold text-[var(--text)] hover:text-[var(--red)]"
      >
        <ArrowLeft size={16} weight="bold" /> Back to shop
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="card-panel p-2">
          <div
            className="rounded-2xl aspect-square border-2 border-[var(--text)] flex items-center justify-center"
            style={tintFor(product.category)}
          >
            <Icon size={220} weight="regular" color={colour} />
          </div>
        </div>
        <div>
          <span className="label-eyebrow">{product.brand} · {product.category}{product.strain ? ` · ${product.strain}` : ""}</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[0.95]">{product.name}</h1>
          <p className="mt-6 text-[var(--text-2)] leading-relaxed max-w-lg">{product.description}</p>
          {(product.thc_pct > 0 || product.cbd_pct > 0) && (
            <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
              <div className="panel-admin p-4 text-center" style={{ background: "var(--yellow)" }}>
                <span className="label-eyebrow" style={{ color: "var(--text)" }}>THC</span>
                <p className="font-display text-3xl mt-1">{product.thc_pct}%</p>
              </div>
              <div className="panel-admin p-4 text-center" style={{ background: "var(--green)", color: "#fff" }}>
                <span className="label-eyebrow" style={{ color: "#fff" }}>CBD</span>
                <p className="font-display text-3xl mt-1">{product.cbd_pct}%</p>
              </div>
              <div className="panel-admin p-4 text-center">
                <span className="label-eyebrow">Stock</span>
                <p className="font-display text-3xl mt-1">{product.inventory}</p>
              </div>
            </div>
          )}
          <div className="mt-8 flex items-center gap-6">
            <span className="font-display text-5xl">R{product.price.toFixed(2)}</span>
            <button
              data-testid={TID.addToCartBtn}
              onClick={onAdd}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> Add to basket
            </button>
          </div>
          <div className="mt-8 flex items-center gap-3">
            <span className="chip">In stock · {product.inventory}</span>
            <span className="chip" style={{ background: "var(--yellow)" }}>★ {product.rating.toFixed(1)}</span>
          </div>
        </div>
      </div>

      {reviews.length > 0 && (
        <section>
          <h2 className="font-display text-3xl mb-4">Customer reviews</h2>
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.review_id} className="panel-admin p-5">
                <div className="flex items-center gap-2">
                  <span className="font-black">{r.user_name}</span>
                  <span className="text-[var(--yellow-dark)]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                </div>
                <p className="mt-2 text-[var(--text-2)] text-sm">{r.comment}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
